import { useState, useEffect, useCallback } from 'react';
import config from '../config';

// Update the config file to include reports API URL if not already there
// This should be defined in your config file
// config.reportsApiUrl = `${config.apiUrl}/rapports`;

// Cache management for reports
const reportsCache = {
  data: null,
  clientData: null,
  financeData: null,
  reservationsData: null,
  timestamp: 0,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

// Helper function to check if cache is valid
const isCacheValid = (key) => {
  return (
    reportsCache[key] &&
    Date.now() - reportsCache.timestamp < reportsCache.CACHE_DURATION
  );
};

// Helper for API calls
const apiCall = async (url, options = {}) => {
  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
  
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      signal: controller.signal,
      ...options,
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn(`API request timeout for ${url}`);
      throw new Error(`Request timeout: ${url}`);
    }
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.warn(`Network error for ${url}`);
      throw new Error(`Network error: Unable to connect to server`);
    }
    
    throw error;
  }
};

// Hook to get reports list
export const useReportsData = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (isCacheValid('data')) {
        setReports(reportsCache.data);
        setLoading(false);
        return;
      }

      // Fetch from API
      const response = await apiCall(`${config.apiUrl}/rapports`);
      const reportsData = response.data || [];

      // Cache the results
      reportsCache.data = reportsData;
      reportsCache.timestamp = Date.now();

      setReports(reportsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError(error.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();

    // Listen for data updates
    const handleUpdate = () => fetchReports();
    window.addEventListener('reportsDataUpdate', handleUpdate);

    return () => {
      window.removeEventListener('reportsDataUpdate', handleUpdate);
    };
  }, [fetchReports]);

  return { reports, loading, error, refreshReports: fetchReports };
};

// Hook to get client data for reports
export const useClientsReportData = () => {
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClientsReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (isCacheValid('clientData')) {
        setClientsData(reportsCache.clientData);
        setLoading(false);
        return;
      }

      // Fetch from API
      const response = await apiCall(`${config.apiUrl}/rapports/data/clients`);
      const data = response.data || [];

      // Cache the results
      reportsCache.clientData = data;
      reportsCache.timestamp = Date.now();

      setClientsData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients report data:', error);
      setError(error.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientsReportData();

    // Listen for data updates
    const handleUpdate = () => fetchClientsReportData();
    window.addEventListener('clientsDataUpdate', handleUpdate);
    window.addEventListener('reportsDataUpdate', handleUpdate);

    return () => {
      window.removeEventListener('clientsDataUpdate', handleUpdate);
      window.removeEventListener('reportsDataUpdate', handleUpdate);
    };
  }, [fetchClientsReportData]);

  return { clientsData, loading, error };
};

// Hook to get finance data for reports
export const useFinancesReportData = () => {
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFinancesReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (isCacheValid('financeData')) {
        setFinanceData(reportsCache.financeData);
        setLoading(false);
        return;
      }

      // Fetch from API
      const response = await apiCall(`${config.apiUrl}/rapports/data/finances`);
      const data = response.data || {};

      // Cache the results
      reportsCache.financeData = data;
      reportsCache.timestamp = Date.now();

      setFinanceData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching finance report data:', error);
      setError(error.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFinancesReportData();

    // Listen for data updates
    const handleUpdate = () => fetchFinancesReportData();
    window.addEventListener('financialDataUpdate', handleUpdate);
    window.addEventListener('reportsDataUpdate', handleUpdate);

    return () => {
      window.removeEventListener('financialDataUpdate', handleUpdate);
      window.removeEventListener('reportsDataUpdate', handleUpdate);
    };
  }, [fetchFinancesReportData]);

  return { financeData, loading, error };
};

// Hook to get reservations data for reports
export const useReservationsReportData = () => {
  const [reservationsData, setReservationsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReservationsReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (isCacheValid('reservationsData')) {
        setReservationsData(reportsCache.reservationsData);
        setLoading(false);
        return;
      }

      // Fetch from API
      const response = await apiCall(`${config.apiUrl}/rapports/data/reservations`);
      const data = response.data || {};

      // Cache the results
      reportsCache.reservationsData = data;
      reportsCache.timestamp = Date.now();

      setReservationsData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reservations report data:', error);
      setError(error.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReservationsReportData();

    // Listen for data updates
    const handleUpdate = () => fetchReservationsReportData();
    window.addEventListener('reservationsDataUpdate', handleUpdate);
    window.addEventListener('reportsDataUpdate', handleUpdate);

    return () => {
      window.removeEventListener('reservationsDataUpdate', handleUpdate);
      window.removeEventListener('reportsDataUpdate', handleUpdate);
    };
  }, [fetchReservationsReportData]);

  return { reservationsData, loading, error };
};

// Function to create a new report
export const createReport = async (reportData) => {
  try {
    const result = await apiCall(`${config.apiUrl}/rapports`, {
      method: 'POST',
      body: JSON.stringify(reportData)
    });
    
    // Invalidate cache and notify about data change
    reportsCache.data = null;
    reportsCache.timestamp = 0;
    window.dispatchEvent(new CustomEvent('reportsDataUpdate'));
    
    return result.data;
  } catch (error) {
    console.error('Error creating report:', error);
    throw error;
  }
};

// Function to export a report
export const exportReport = async (exportData) => {
  try {
    const result = await apiCall(`${config.apiUrl}/rapports/export`, {
      method: 'POST',
      body: JSON.stringify(exportData)
    });
    
    // Invalidate cache and notify about data change
    reportsCache.data = null;
    reportsCache.timestamp = 0;
    window.dispatchEvent(new CustomEvent('reportsDataUpdate'));
    
    return result.data;
  } catch (error) {
    console.error('Error exporting report:', error);
    throw error;
  }
};

// Function to delete a report
export const deleteReport = async (reportId) => {
  try {
    await apiCall(`${config.apiUrl}/rapports/${reportId}`, {
      method: 'DELETE'
    });
    
    // Invalidate cache and notify about data change
    reportsCache.data = null;
    reportsCache.timestamp = 0;
    window.dispatchEvent(new CustomEvent('reportsDataUpdate'));
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting report:', error);
    throw error;
  }
};
